/** Render jednoho konfiguračního pole widgetu (WidgetFieldSpec → input). Sdílené editorem. */

import { X } from "lucide-react";
import React from "react";
import type { WidgetFieldSpec } from "@globals/layouts";
import { TopicPicker } from "@client/components/topics/TopicPicker";
import { concretizeTopic, isTemplatedTopic, templatizeTopic } from "@client/lib/deviceTopic";

const RANGE_OPTIONS = [
  { value: "1h", label: "1 hodina" },
  { value: "6h", label: "6 hodin" },
  { value: "1d", label: "1 den" },
  { value: "7d", label: "7 dní" },
  { value: "30d", label: "30 dní" },
];

const base = "w-full rounded border border-input bg-background px-2 py-1 text-xs";

export const FieldRenderer: React.FC<{
  spec: WidgetFieldSpec;
  value: unknown;
  onChange: (v: unknown) => void;
  /** Device layout: typ + slug aktuálního zařízení → umožní {slug} přepínač u topic polí. */
  deviceType?: string;
  slug?: string;
}> = ({ spec, value, onChange, deviceType, slug }) => {
  const label = <span className="text-[11px] font-medium text-muted-foreground">{spec.label}</span>;
  const canTemplate = !!deviceType && !!slug;

  if (spec.kind === "topic") {
    const raw = String(value ?? "");
    const templated = isTemplatedTopic(raw);
    // Výběr emituje konkrétní DB topic → když je „sdílet" zapnuto (nebo už byl {slug}), přepiš na {slug}.
    const onPick = (v: string) => onChange(templated ? templatizeTopic(v, deviceType, slug) : v);
    return (
      <label className="flex flex-col gap-1">
        {label}
        <TopicPicker value={concretizeTopic(raw, deviceType, slug)} onChange={onPick} placeholder={spec.placeholder} />
        {canTemplate && (
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <input type="checkbox" checked={templated} onChange={(e) => onChange(e.target.checked ? templatizeTopic(raw, deviceType, slug) : concretizeTopic(raw, deviceType, slug))} />
            Sdílet napříč zařízeními ({"{slug}"})
          </span>
        )}
      </label>
    );
  }
  if (spec.kind === "topics") {
    const list = Array.isArray(value) ? (value as string[]) : [];
    const templated = list.length > 0 && list.some((t) => isTemplatedTopic(t));
    const setItem = (i: number, v: string) => onChange(list.map((x, idx) => (idx === i ? (templated ? templatizeTopic(v, deviceType, slug) : v) : x)));
    return (
      <div className="flex flex-col gap-1">
        {label}
        {list.map((tpc, i) => (
          <div key={i} className="flex items-center gap-1">
            <div className="min-w-0 flex-1">
              <TopicPicker value={concretizeTopic(tpc, deviceType, slug)} onChange={(v) => setItem(i, v)} />
            </div>
            <button type="button" className="text-destructive" onClick={() => onChange(list.filter((_, idx) => idx !== i))} aria-label="Odebrat">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <button type="button" className="self-start rounded border border-input px-2 py-0.5 text-[11px]" onClick={() => onChange([...list, ""])}>
            + veličina
          </button>
          {canTemplate && list.length > 0 && (
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                checked={templated}
                onChange={(e) => onChange(list.map((t) => (e.target.checked ? templatizeTopic(t, deviceType, slug) : concretizeTopic(t, deviceType, slug))))}
              />
              Sdílet ({"{slug}"})
            </span>
          )}
        </div>
      </div>
    );
  }
  if (spec.kind === "number") {
    return (
      <label className="flex flex-col gap-1">
        {label}
        <input type="number" className={base} min={spec.min} max={spec.max} step={spec.step} value={value === undefined || value === null ? "" : Number(value)} onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))} />
      </label>
    );
  }
  if (spec.kind === "bool") {
    return (
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
    );
  }
  if (spec.kind === "select") {
    return (
      <label className="flex flex-col gap-1">
        {label}
        <select className={base} value={String(value ?? spec.options[0]?.value ?? "")} onChange={(e) => onChange(e.target.value)}>
          {spec.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (spec.kind === "chartRange") {
    return (
      <label className="flex flex-col gap-1">
        {label}
        <select className={base} value={String(value ?? "1d")} onChange={(e) => onChange(e.target.value)}>
          {RANGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  // text
  return (
    <label className="flex flex-col gap-1">
      {label}
      <input className={base} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} placeholder={spec.kind === "text" ? spec.placeholder : undefined} />
    </label>
  );
};
